import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        alert(isRTL 
          ? `شكراً! تم إرسال رسالتك بنجاح. سنتواصل معك قريباً على ${formData.email}` 
          : `Thank you! Your message has been sent successfully. We will contact you soon at ${formData.email}`);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        alert(response.data.message || (isRTL 
          ? 'عذراً، حدث خطأ في إرسال رسالتك. يرجى المحاولة مرة أخرى أو مراسلتنا مباشرة على queries@targethealth.ae' 
          : 'Sorry, there was an error sending your message. Please try again or email us directly at queries@targethealth.ae'));
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert(isRTL 
        ? 'عذراً، حدث خطأ في إرسال رسالتك. يرجى المحاولة مرة أخرى أو مراسلتنا مباشرة على queries@targethealth.ae' 
        : 'Sorry, there was an error sending your message. Please try again or email us directly at queries@targethealth.ae');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <div 
        className="relative h-80 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/5234508/pexels-photo-5234508.jpeg?w=1920&q=80)' }}
      >
        <div className="absolute inset-0 bg-slate-900 bg-opacity-60 flex items-center justify-center">
          {/* Logo Watermark */}
          <img 
            src="/logo.png" 
            alt="Target Health" 
            className="absolute bottom-6 left-6 w-40 opacity-20"
          />
          <h1 className="text-5xl font-bold text-white z-10">{t.contact.title}</h1>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-2">
              {isRTL ? t.contact.mainTitle : 'GET IN'} <span className="text-cyan-500">{isRTL ? '' : 'TOUCH'}</span>
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-4"></div>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t.contact.subtitle}
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className={`grid md:grid-cols-2 gap-12 ${isRTL ? 'md:grid-flow-dense' : ''}`}>
              {/* Contact Information */}
              <div className={isRTL ? 'md:order-2' : ''}>
                <h3 className="text-2xl font-bold text-slate-800 mb-6">{t.contact.contactInfo}</h3>
                
                <div className="space-y-6">
                  <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="bg-cyan-500 w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <Phone size={24} className="text-white" />
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <h4 className="font-bold text-slate-800 mb-1">{t.contact.phoneFax}</h4>
                      <p className="text-slate-600">
                        {isRTL ? 'هاتف: ' : 'Phone: '}
                        <bdi>0567477759, 026221300</bdi>
                      </p>
                      <p className="text-slate-600">
                        {isRTL ? 'فاكس: ' : 'Fax: '}
                        <bdi>{companyInfo.fax}</bdi>
                      </p>
                    </div>
                  </div>

                  <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="bg-cyan-500 w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <Mail size={24} className="text-white" />
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <h4 className="font-bold text-slate-800 mb-1">{t.contact.email}</h4>
                      <p className="text-slate-600" dir="ltr">{companyInfo.email}</p>
                    </div>
                  </div>

                  <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="bg-cyan-500 w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} className="text-white" />
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <h4 className="font-bold text-slate-800 mb-1">{t.contact.address}</h4>
                      <p className="text-slate-600 mb-3">{isRTL ? companyInfo.addressArabic : companyInfo.address}</p>
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=Target+for+Health+Consulting+Abu+Dhabi+UAE"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-md transition-colors"
                      >
                        <MapPin size={18} />
                        {isRTL ? 'افتح في الخرائط' : 'Open in Maps'}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Map Pin Icon - Click to Open Map */}
                <div className="mt-8">
                  <button
                    onClick={() => setIsMapModalOpen(true)}
                    className="w-full bg-gradient-to-br from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 rounded-lg p-12 flex flex-col items-center justify-center gap-4 transition-all border-2 border-slate-300 hover:border-cyan-500 group"
                  >
                    <div className="bg-red-500 group-hover:bg-red-600 rounded-full p-6 shadow-lg transition-all group-hover:scale-110">
                      <MapPin size={48} className="text-white" />
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-slate-800 mb-1">
                        {isRTL ? 'عرض الموقع على الخريطة' : 'View Location on Map'}
                      </p>
                      <p className="text-sm text-slate-600">
                        {isRTL ? 'انقر لفتح الخريطة' : 'Click to open map'}
                      </p>
                    </div>
                  </button>
                </div>

                {/* Map Modal */}
                {isMapModalOpen && (
                  <div 
                    className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
                    onClick={() => setIsMapModalOpen(false)}
                  >
                    <div 
                      className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[600px] relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Modal Header */}
                      <div className="bg-cyan-500 text-white p-4 rounded-t-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <MapPin size={24} />
                          <h3 className="text-xl font-bold">
                            {isRTL ? 'موقع Target للاستشارات الصحية' : 'Target for Health Consulting Location'}
                          </h3>
                        </div>
                        <button
                          onClick={() => setIsMapModalOpen(false)}
                          className="hover:bg-cyan-600 rounded-full p-2 transition-colors"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      {/* Map Container with Overlay */}
                      <div className="h-[calc(100%-64px)] relative">
                        <iframe
                          src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Al+Saada+Tower+Al+Mamoura+Abu+Dhabi+UAE&zoom=16"
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title={isRTL ? "موقع الشركة" : "Company Location"}
                        ></iframe>
                        
                        {/* Custom Company Name Overlay */}
                        <div className="absolute top-4 right-8 z-10 pointer-events-none">
                          <div className="bg-white shadow-2xl rounded-lg px-6 py-4 border-2 border-red-500">
                            <div className="flex items-center gap-3">
                              <div className="bg-red-500 rounded-full p-2">
                                <MapPin size={20} className="text-white" />
                              </div>
                              <div>
                                <p className="font-bold text-slate-800 text-lg">
                                  {isRTL ? 'Target للاستشارات الصحية' : 'Target for Health Consulting'}
                                </p>
                                <p className="text-sm text-slate-600">
                                  {isRTL ? 'مكتب 1102، برج السعادة' : 'Office 1102, Al Saada Tower'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Form */}
              <div className={isRTL ? 'md:order-1' : ''}>
                <h3 className="text-2xl font-bold text-slate-800 mb-6">{t.contact.sendMessage}</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-slate-700 font-semibold mb-2">
                      {t.contact.fullName} {t.contact.required}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                      placeholder={isRTL ? 'اسمك' : 'Your name'}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-slate-700 font-semibold mb-2">
                      {t.contact.emailAddress} {t.contact.required}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-slate-700 font-semibold mb-2">
                      {t.contact.phoneNumber}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                      placeholder="+971 XX XXX XXXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-slate-700 font-semibold mb-2">
                      {t.contact.subject} {t.contact.required}
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                      placeholder={isRTL ? 'كيف يمكننا مساعدتك؟' : 'How can we help?'}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-slate-700 font-semibold mb-2">
                      {t.contact.message} {t.contact.required}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all resize-none"
                      placeholder={isRTL ? 'رسالتك...' : 'Your message...'}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-semibold transition-colors flex items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''} ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Send size={20} />
                    {isSubmitting ? t.contact.sending : t.contact.sendBtn}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;